import React from 'react';
import { Layout, Menu, Button, Space, Tag, Drawer } from 'antd';
import { LogoutOutlined, MenuOutlined, CloseOutlined, SunOutlined, MoonOutlined, MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';

const { Header, Content, Footer, Sider } = Layout;

export default function SidebarLayout({
  isMobile,
  collapsed,
  setCollapsed,
  mobileDrawerOpen,
  setMobileDrawerOpen,
  activeMenu,
  handleMenuClick,
  menuItems,
  user,
  role,
  sessionTimeLeft,
  currentThemeKey,
  setCurrentThemeKey,
  handleLogout,
  logoGif,
  getPageHeading,
  children
}) {
  const isDarkMode = currentThemeKey === 'dark';
  const toggleTheme = () => setCurrentThemeKey(isDarkMode ? 'light' : 'dark');

  const renderSidebarContent = () => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{
        height: 64,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: collapsed && !isMobile ? '0 8px' : '0 16px',
        borderBottom: '1px solid var(--border-color)',
        overflow: 'hidden',
        position: 'relative',
        flexShrink: 0
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%'
        }}>
          <img
            src={logoGif}
            alt="Vedika Logo"
            style={{
              height: collapsed && !isMobile ? 36 : 44,
              maxWidth: collapsed && !isMobile ? '100%' : '85%',
              width: 'auto',
              objectFit: 'contain',
              display: 'block'
            }}
          />
        </div>
        {isMobile && (
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={() => setMobileDrawerOpen(false)}
            style={{ color: 'var(--text-color)', position: 'absolute', right: 12 }}
          />
        )}
      </div>

      {isMobile && (
        <div style={{
          padding: '12px 16px',
          borderBottom: '1px solid var(--border-color)',
          background: 'rgba(0,0,0,0.05)',
          flexShrink: 0
        }}>
          <div style={{ color: 'var(--text-color)', fontSize: 13 }}>
            Logged in as: <strong style={{ color: 'var(--primary-color)' }}>{user?.login_name || user?.name || user?.username}</strong>
          </div>
          <Tag color="blue" style={{ marginTop: 4 }}>{role}</Tag>
          {sessionTimeLeft && (
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
              ⏱ Session: <strong style={{ fontFamily: 'monospace' }}>{sessionTimeLeft}</strong>
            </div>
          )}
        </div>
      )}

      {/* Scrollable Menu Area */}
      <div
        className="sidebar-menu-scroll-container"
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          minHeight: 0
        }}
      >
        <Menu
          mode="inline"
          selectedKeys={[activeMenu]}
          onClick={handleMenuClick}
          style={{ background: 'transparent', padding: '12px 0', borderRight: 'none' }}
          items={menuItems}
        />
      </div>

      {isMobile && (
        <div style={{
          padding: '12px 16px',
          borderTop: '1px solid var(--border-color)',
          marginTop: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          flexShrink: 0
        }}>
          <Button
            type="default"
            icon={isDarkMode ? <SunOutlined /> : <MoonOutlined />}
            onClick={() => {
              toggleTheme();
              if (isMobile) setMobileDrawerOpen(false);
            }}
            style={{ width: '100%', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            {isDarkMode ? 'Light Mode' : 'Dark Mode'}
          </Button>
          <Button
            type="primary"
            danger
            icon={<LogoutOutlined />}
            onClick={handleLogout}
            style={{ width: '100%', marginTop: 6, borderRadius: 6 }}
          >
            Sign Out
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <Layout style={{ minHeight: '100vh', background: 'var(--bg-color)' }}>
      {/* DESKTOP SIDER */}
      {!isMobile && (
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={(value) => setCollapsed(value)}
          width={260}
          className="vedika-desktop-sider"
          style={{
            background: 'var(--panel-color)',
            borderRight: '1px solid var(--border-color)',
            position: 'sticky',
            top: 0,
            height: '100vh',
            maxHeight: '100vh',
            overflow: 'hidden'
          }}
        >
          {renderSidebarContent()}
        </Sider>
      )}

      {/* MOBILE DRAWER */}
      {isMobile && (
        <Drawer
          placement="left"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          closable={false}
          width={280}
          bodyStyle={{ padding: 0, background: 'var(--panel-color)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        >
          {renderSidebarContent()}
        </Drawer>
      )}

      <Layout style={{ background: 'var(--bg-color)' }}>
        {/* HEADER */}
        <Header
          style={{
            background: 'var(--panel-color)',
            padding: isMobile ? '0 12px' : '0 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid var(--border-color)',
            position: 'sticky',
            top: 0,
            zIndex: 100,
            height: isMobile ? 52 : 64
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isMobile ? (
              <Button
                type="text"
                icon={<MenuOutlined style={{ fontSize: 20 }} />}
                onClick={() => setMobileDrawerOpen(true)}
                style={{ color: 'var(--text-color)', padding: '0 4px' }}
              />
            ) : (
              <Button
                type="text"
                icon={collapsed ? <MenuUnfoldOutlined style={{ fontSize: 18 }} /> : <MenuFoldOutlined style={{ fontSize: 18 }} />}
                onClick={() => setCollapsed(!collapsed)}
                style={{ color: 'var(--text-color)', padding: '0 4px', marginRight: 4 }}
                title={collapsed ? 'Expand menu' : 'Minimize menu'}
              />
            )}
            <span style={{
              color: 'var(--text-color)',
              fontSize: isMobile ? 15 : 18,
              fontWeight: 700,
              whiteSpace: 'nowrap',
              letterSpacing: '-0.01em'
            }}>
              {getPageHeading()}
            </span>
          </div>

          {!isMobile ? (
            <Space size="medium">
              <Button
                type="text"
                icon={isDarkMode ? <SunOutlined /> : <MoonOutlined />}
                onClick={toggleTheme}
                style={{ color: 'var(--text-color)', fontSize: 16 }}
              />

              {sessionTimeLeft && (
                <Tag color="gold" style={{ fontSize: 13, padding: '4px 8px', borderRadius: 4, border: 'none', background: 'rgba(250, 173, 20, 0.1)', color: '#faad14' }}>
                  ⏱ {sessionTimeLeft}
                </Tag>
              )}
              <span style={{ color: 'var(--text-color)' }}>
                <strong style={{ color: 'var(--primary-color)' }}>{user?.login_name || user?.name || user?.username}</strong> <Tag color="blue">{role}</Tag>
              </span>
              <Button
                type="primary"
                danger
                icon={<LogoutOutlined />}
                onClick={handleLogout}
                style={{ borderRadius: 6 }}
              >
                Sign Out
              </Button>
            </Space>
          ) : (
            <Space size="small">
              <Button
                type="text"
                icon={isDarkMode ? <SunOutlined /> : <MoonOutlined />}
                onClick={toggleTheme}
                size="small"
                style={{ color: 'var(--text-color)', fontSize: 16 }}
              />
              <span style={{ color: 'var(--primary-color)', fontWeight: 600, fontSize: 13 }}>
                {user?.login_name || user?.name || user?.username}
              </span>
              <Button
                type="primary"
                danger
                icon={<LogoutOutlined />}
                onClick={handleLogout}
                size="small"
                style={{ borderRadius: 6 }}
              />
            </Space>
          )}
        </Header>

        {/* CONTENT */}
        <Content style={{
          padding: isMobile ? '10px 8px' : '24px',
          overflowY: 'auto',
          paddingBottom: isMobile ? 80 : 24
        }}>
          <div className="layout-content-wrapper" style={{ minHeight: 480, color: 'var(--text-color)' }}>
            {children}
          </div>
        </Content>

        {!isMobile && (
          <Footer style={{
            textAlign: 'center',
            background: 'var(--bg-color)',
            color: 'var(--text-secondary)',
            borderTop: '1px solid var(--border-color)',
            padding: '16px 0'
          }}>
            Designed By <a href="https://www.linkedin.com/in/henry-monteiro-573935147/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-secondary)', fontWeight: 'bold' }}>HENRY MONTEIRO</a> ©2026 - {new Date().getFullYear()}
          </Footer>
        )}
      </Layout>
    </Layout>
  );
}
